import { Component, OnInit, Inject, DestroyRef, inject, HostListener } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import * as XLSX from 'xlsx';

import { InvoiceService, InvoiceParams } from '../../core/services/invoice.service';
import { ReadInvoiceService, ReadInvoiceParams } from '../../core/services/read-invoice.service';
import {
  InvoiceItService,
  InvoiceItGetParams,
  InvoiceItInsertParams,
} from '../../core/services/invoice-it.service';
import { InvoiceOpService, InvoiceOpParams } from '../../core/services/invoice-op.service';
import { ErpService, ErpParams } from '../../core/services/erp.service';
import { PdfService } from '../../core/services/pdf.service';
import { FileSaverService } from '../../core/services/file-saver.service';
import { OauthUtilService } from '../../core/services/oauth-util.service';
import { DialogService } from '../../core/services/dialog.service';
import { ModalService } from '../../core/services/modal.service';
import {
  EditInvoiceModalComponent,
  InvoiceData,
} from '../../shared/components/modal/edit-invoice-modal.component';
import { MomentPipe } from '../../shared/pipes/moment.pipe';
import { SwedishNumberPipe } from '../../shared/pipes/swedish-number.pipe';
import {
  InvoiceCustMode,
  InvoiceableItem,
  InvoiceListItem,
  InvoiceStatement,
  InvoiceStatementDetail,
  InvoiceItGetResponse,
  InvoiceItInvoice,
  InvoiceInsertParams,
  ErpInvoiceInfo,
  ErpInvoiceRow,
  InvoiceUpdateBody,
  InvoiceGroupMode,
  InvoiceDetailMode,
} from './ng-invoice.interfaces';

@Component({
  selector: 'app-ng-invoice',
  standalone: false,
  template: require('./ng-invoice.component.html').default,
  styles: [require('!!raw-loader!sass-loader!./ng-invoice.component.scss').default],
})
export class NgInvoiceComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  openActionMenuKey: string | null = null;

  // Date range
  dateFrom: moment.Moment;
  dateTo: moment.Moment;

  // Modes
  groupMode: InvoiceGroupMode = 'K';
  custMode: InvoiceCustMode = { external: true, internal: false };
  detailMode: InvoiceDetailMode = 'P';

  // Data
  invoice: InvoiceableItem[] = [];
  invoiceList: InvoiceListItem[] = [];

  // Totals
  totAmm1 = 0;
  totAmm2 = 0;

  // Headers
  header: string[] = [];
  header2: string[] = ['Kund', 'Projekt', 'Datum', 'Period start', 'Period stop', 'Summa'];

  private normalizeStatementForStorage<T extends Record<string, any>>(statement: T, invoiceDate: string): T {
    const normalized = { ...statement, invoice_date: invoiceDate };
    delete normalized.inputdate;
    return normalized;
  }

  constructor(
    @Inject(InvoiceService) private invoiceService: InvoiceService,
    @Inject(ReadInvoiceService) private readInvoiceService: ReadInvoiceService,
    @Inject(InvoiceItService) private invoiceItService: InvoiceItService,
    @Inject(InvoiceOpService) private invoiceOpService: InvoiceOpService,
    @Inject(ErpService) private erpService: ErpService,
    @Inject(PdfService) private pdfService: PdfService,
    @Inject(FileSaverService) private fileSaverService: FileSaverService,
    @Inject(OauthUtilService) private oauthUtilService: OauthUtilService,
    @Inject(DialogService) private dialogService: DialogService,
    @Inject(ModalService) private modalService: ModalService
  ) {
    // Initialize dates logic similar to legacy
    if (moment().date() > 15) {
      this.dateFrom = moment().startOf('month');
    } else {
      this.dateFrom = moment().startOf('month').subtract(1, 'month');
    }
    this.dateTo = moment(this.dateFrom).add(1, 'month').subtract(1, 'days');
  }

  ngOnInit(): void {
    this.oauthUtilService.getAccessToken();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.report-row-action-menu')) {
      this.openActionMenuKey = null;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openActionMenuKey = null;
  }

  updatePeriod(direction: number): void {
    const start = moment(this.dateFrom);
    const stop = moment(this.dateTo);

    if (direction === -1) {
      start.startOf('month').subtract(1, 'months');
      stop.startOf('month').subtract(1, 'days');
    } else {
      start.startOf('month').add(1, 'months');
      stop.startOf('month').add(2, 'months').subtract(1, 'days');
    }

    this.dateFrom = start;
    this.dateTo = stop;
  }

  toggleActionMenu(key: string, event: MouseEvent): void {
    event.stopPropagation();
    this.openActionMenuKey = this.openActionMenuKey === key ? null : key;
  }

  closeActionMenu(): void {
    this.openActionMenuKey = null;
  }

  fetch(): void {
    this.totAmm1 = 0;
    this.fetchInvoiceAble();
    this.fetchInvoices();
  }

  fetchInvoiceAble(): void {
    switch (this.groupMode) {
      case 'C':
        this.header = ['Kund'];
        break;
      case 'K':
        this.header = ['Kund', 'Projekt', 'Aktivitet', 'Namn', 'Timmar', 'Timpris', 'Summa'];
        break;
      case 'A':
        this.header = ['Kund', 'Projekt', 'Aktivitet', 'Timmar', 'Timpris', 'Summa'];
        break;
      case 'P':
        this.header = ['Kund', 'Projekt', 'Timmar', 'Timpris', 'Summa'];
        break;
      case 'KU':
        this.header = ['Kund', 'Timmar', 'Timpris', 'Summa'];
        break;
      default:
        break;
    }

    const params: InvoiceParams = {
      subTotal: undefined, // Not used in legacy fetch?
      sum: 'A',
      custMode: this.custMode,
      per: 'A',
      type: this.groupMode,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.invoiceService.get<InvoiceableItem>(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.invoice = data;
      let totAmount = 0;

      this.invoice.forEach((item) => {
        if (item.sum && item.sum !== 0) {
          item.timpris = Math.round((item.pris / item.sum + Number.EPSILON) * 100) / 100;
        } else {
          item.timpris = null;
        }

        if (!item.pris) item.timpris = null;
        if (item.customername === null) item.customername = 'Tot';
        if (item.project_name === undefined) item.project_name = undefined;
        if (item.activity_name === undefined) item.activity_name = undefined;

        item.invoiceit = false;
        totAmount += Number(item.pris);
      });

      this.totAmm1 = totAmount;
    });
  }

  fetchInvoices(): void {
    this.totAmm2 = 0;

    // Legacy uses selectOptions.customer/project which are not in the template shown?
    // Ah, invoice.html doesn't show customer/project selects for filtering created invoices.
    // But invoice.js has logic for it.
    // "var cust, proj; if (!$scope.selectOptions.customer) ..."
    // But where are selectOptions set?
    // In ProjectSummaryReport they are used. In InvoiceCtrl they are initialized empty.
    // And I don't see them in invoice.html.
    // So they are likely always null.

    const params: ReadInvoiceParams = {
      cust: null,
      proj: null,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
    };

    this.readInvoiceService.get<InvoiceListItem>(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.invoiceList = data;
      let totAmount = 0;

      this.invoiceList.forEach((item) => {
        totAmount += Number(item.amount);
      });

      this.totAmm2 = Math.round((totAmount + Number.EPSILON) * 100) / 100;
    });
  }

  createInvoice(): void {
    const promises: Promise<void>[] = [];

    const selectedItems = this.invoice.filter((item) => item.invoiceit);

    selectedItems.forEach((item) => {
      const params: InvoiceItGetParams = {
        from: this.dateFrom.format('YYYY-MM-DD'),
        to: this.dateTo.format('YYYY-MM-DD'),
        groupMode: this.groupMode,
        detailMode: this.detailMode,
      };

      switch (this.groupMode) {
        case 'K':
          params.cust_id = item.customer_id;
          params.proj_id = item.project_id;
          params.act_id = item.activity_id;
          params.emp_id = item.employee_id;
          break;
        case 'A':
          params.cust_id = item.customer_id;
          params.proj_id = item.project_id;
          params.act_id = item.activity_id;
          break;
        case 'P':
          params.cust_id = item.customer_id;
          params.proj_id = item.project_id;
          break;
        case 'C':
        case 'KU':
          params.cust_id = item.customer_id;
          break;
      }

      const p = new Promise<void>((resolve, reject) => {
        this.invoiceItService.get<InvoiceItGetResponse>(params).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
          (data) => {
            if (data && data.length > 0) {
              const invoiceIt = data[0].invoice;
              const invoiceItDetails = data[0].invoice_detail;
              const companyInfo = data[0].company[0];

              const insertPromises: Promise<void>[] = [];

              invoiceIt.forEach((inv: InvoiceItInvoice) => {
                inv.companyInfo = companyInfo;
                inv.details = invoiceItDetails;
                inv.detailMode = params.detailMode;
                const invoiceDate = moment().startOf('month').format('YYYY-MM-DD');
                const statementPayload = this.normalizeStatementForStorage(inv as unknown as Record<string, any>, invoiceDate);

                const invParams: InvoiceInsertParams = {
                  detailMode: params.detailMode || 'P',
                  invoice_date: invoiceDate,
                  customer_id: inv.customer_id,
                  project_id: inv.project_id,
                  activity_id: inv.activity_id,
                  employee_id: inv.employee_id,
                  statement: JSON.stringify(statementPayload),
                  amount: inv.pris,
                  payment_term: inv.payment_term,
                };

                const insertParams: InvoiceItInsertParams = {
                  cust_id: inv.customer_id,
                  from: this.dateFrom.format('YYYY-MM-DD'),
                  to: this.dateTo.format('YYYY-MM-DD'),
                  groupMode: this.groupMode,
                };

                const insertP = new Promise<void>((res, rej) => {
                  this.invoiceItService.insert(insertParams, invParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
                    next: () => res(),
                    error: (err) => rej(err),
                  });
                });
                insertPromises.push(insertP);
              });

              Promise.all(insertPromises)
                .then(() => resolve())
                .catch((err) => reject(err));
            } else {
              resolve();
            }
          },
          (err) => reject(err)
        );
      });
      promises.push(p);
    });

    Promise.all(promises).then(() => {
      this.invoice = this.invoice.filter((item) => !item.invoiceit);
      this.fetchInvoices();
    });
  }

  // Helper for table display
  getValue(head: string, inv: InvoiceableItem): string | number | boolean | null | undefined {
    switch (head) {
      case 'Kund':
        return inv.customername + (inv.endcustomer ? '-' + inv.endcustomer : '');
      case 'Projekt':
        return inv.project_name;
      case 'Aktivitet':
        return inv.activity_name;
      case 'Namn':
        return inv.name;
      case 'Timmar':
        return inv.sum;
      case 'Timpris':
        return inv.timpris;
      case 'Summa':
        return inv.pris;
      case 'Fakt':
        return inv.invoiceit;
      default:
        return '';
    }
  }

  // Helper for invoice list display
  getListValue(head: string, inv: InvoiceListItem): string | number | undefined {
    switch (head) {
      case 'Kund':
        return inv.customername + (inv.endcustomer ? '-' + inv.endcustomer : '');
      case 'Projekt':
        return inv.project_name;
      case 'Kund Id':
        return inv.customer_id;
      case 'Projekt Id':
        return inv.project_id;
      case 'Period start':
        return inv.period_start;
      case 'Period stop':
        return inv.period_stop;
      case 'Datum':
        return inv.invoice_date;
      case 'Summa':
        return inv.amount;
      default:
        return '';
    }
  }

  viewPdf(inv: InvoiceListItem): void {
    const invoiceClone = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      period_start: moment(inv.period_start).format('YYYY-MM-DD'),
      period_stop: moment(inv.period_stop).format('YYYY-MM-DD'),
    };

    this.pdfService.save(1, { statement: invoiceClone }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.openPdf(data.result);
    });
  }

  downloadPdf(inv: InvoiceListItem): void {
    const invoiceClone = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      period_start: moment(inv.period_start).format('YYYY-MM-DD'),
      period_stop: moment(inv.period_stop).format('YYYY-MM-DD'),
    };

    const statement: InvoiceStatement = JSON.parse(inv.statement);
    const statementType = statement.statementType === 'kredit' ? 'kreditfaktura ' : '';
    const filename = `Kits ${statementType}${inv.invoice_number} ${inv.customername}.pdf`;

    this.pdfService.save(1, { statement: invoiceClone }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.savePdf(data.result, filename);
    });
  }

  private b64toBlob(b64Data: string, contentType: string = '', sliceSize: number = 512): Blob {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  private openPdf(base64doc: string): void {
    const blob = this.b64toBlob(base64doc, 'application/pdf');
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }

  private savePdf(base64doc: string, filename: string): void {
    const blob = this.b64toBlob(base64doc, 'application/pdf');
    this.fileSaverService.save(blob, filename);
  }

  async remove(inv: InvoiceListItem, index: number): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'Är du säker?',
      body: 'Vill du ta bort fakturan?',
      confirmText: 'OK',
      cancelText: 'Avbryt',
      variant: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.invoiceOpService
      .remove({
        statement_id: inv.statement_id,
        invoice_number: inv.invoice_number,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.invoiceList.splice(index, 1);
          this.fetchInvoiceAble();
        },
        error: async (error: { status?: number; error?: { error?: string } }) => {
          const body =
            error?.status === 409
              ? (error.error?.error || 'Det går inte att ta bort en faktura som redan har skickats till Fortnox. Skapa en kreditfaktura i stället.')
              : 'Det gick inte att ta bort fakturan.';

          await this.dialogService.alert({
            title: 'Operationen misslyckades',
            body,
            buttonText: 'OK',
            variant: 'danger',
          });
        },
      });
  }

  kredit(inv: InvoiceListItem, index: number): void {
    const statement: InvoiceStatement = JSON.parse(inv.statement);

    if (statement.statementType === 'kredit') {
      this.dialogService.alert({
        title: 'Ej tillåtet',
        body: 'Det går inte att skapa kredit på en kreditfaktura.',
        buttonText: 'OK',
        variant: 'danger',
      });
      return;
    }

    const invParams = {
      ...inv,
      invoice_date: moment(inv.invoice_date).format('YYYY-MM-DD'),
      amount: -inv.amount,
      statementType: 'kredit' as const,
      statement: '',
    };

    statement.pris = -statement.pris;
    statement.statementType = 'kredit';

    if (statement.details) {
      statement.details.forEach((det) => {
        if (det.pris !== undefined) det.pris = -det.pris;
        det.price = -det.price;
      });
    }

    invParams.statement = JSON.stringify(this.normalizeStatementForStorage(statement as unknown as Record<string, any>, invParams.invoice_date));

    const insertParams: InvoiceItInsertParams = {
      cust_id: inv.customer_id,
      from: this.dateFrom.format('YYYY-MM-DD'),
      to: this.dateTo.format('YYYY-MM-DD'),
      groupMode: this.groupMode,
    };

    this.invoiceItService.insert(insertParams, invParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.fetchInvoices();
    });
  }

  async openEditModal(inv: InvoiceListItem, index: number): Promise<void> {
    const updatedInv = (await this.modalService.show(EditInvoiceModalComponent, {
      headerText: 'Editera faktura',
      closeButtonText: 'Avbryt',
      actionButtonText: 'OK',
      inv: inv as unknown as InvoiceData,
    })) as InvoiceData | undefined;

    if (!updatedInv) {
      return;
    }

    const invoiceDate = updatedInv.invoice_date;
    const normalizedStatement = this.normalizeStatementForStorage(
      updatedInv.statement as unknown as Record<string, any>,
      invoiceDate
    );
    normalizedStatement.pris = 0;
    normalizedStatement.details.forEach((det: InvoiceStatementDetail) => {
      normalizedStatement.pris! += det.sum * det.price;
    });
    updatedInv.amount = normalizedStatement.pris;
    updatedInv.statement = JSON.stringify(normalizedStatement) as unknown as typeof updatedInv.statement;

    this.invoiceList[index] = updatedInv as unknown as InvoiceListItem;
    this.updateInvoiceNo(updatedInv as unknown as InvoiceListItem);
  }

  updateInvoiceNo(inv: InvoiceListItem): void {
    const body: InvoiceUpdateBody = {
      invoice_date: inv.invoice_date,
      amount: inv.amount,
      statement: inv.statement,
    };

    this.invoiceOpService
      .update(
        {
          statement_id: inv.statement_id,
          invoice_number: inv.invoice_number,
        },
        body
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // Success
      });
  }

  sendToErp(inv: InvoiceListItem, index: number): void {
    const statement: InvoiceStatement = JSON.parse(inv.statement);

    const erpInfo: ErpInvoiceInfo = {
      CustomerNumber: statement.ext_customer_id || statement.customername || '',
      DocumentNumber: inv.invoice_number,
      InvoiceDate: moment(inv.invoice_date).format('YYYY-MM-DD'),
      OurReference: statement.ourreference,
      YourReference: statement.extreference,
      YourOrderNumber: statement.extprojectid,
      Remarks: `Fakturan avser tid mellan ${moment(inv.period_start).format('YYYY-MM-DD')} - ${moment(inv.period_stop).format('YYYY-MM-DD')}`,
      Comments: statement.invoice_comment || '',
      InvoiceRows: [],
    };

    if (statement.details) {
      statement.details.forEach((det) => {
        const projectName = det.external_project_name || det.project_name || '';
        const activityName = det.external_activity_name || det.activity_name || '';
        const costcenter = det.activity_costcenter || det.costcenter || '';

        const row: ErpInvoiceRow = {
          ArticleNumber: 1,
          Description: `${projectName} ${det.name || ''} ${activityName}`.trim(),
          DeliveredQuantity: det.sum,
          Price: det.price,
          CostCenter: costcenter,
        };
        erpInfo.InvoiceRows.push(row);
      });
    }

    if (inv.ext_statement_id === null) {
      this.erpService
        .save(
          {
            statement_id: inv.statement_id,
            redirect_uri: this.oauthUtilService.fullUrl(),
          },
          erpInfo
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (success: { id: number }) => {
            inv.ext_statement_id = success.id;
          },
              error: async (error: { status: number; error?: { ErrorInformation?: string }; message?: string }) => {
                if (error.status === 433) {
                  this.oauthUtilService.oauthLoginHandler(error);
                } else {
                  await this.dialogService.alert({
                    title: 'Ajdå, något gick fel ...',
                    body: `${error.status} ${JSON.stringify(error.error?.ErrorInformation || error.message)}`,
                    buttonText: 'OK',
                    variant: 'danger',
                  });
                }
              },
            });
    } else {
      this.confirmAndUpdateErp(inv, erpInfo);
    }
  }

  private async confirmAndUpdateErp(inv: InvoiceListItem, erpInfo: ErpInvoiceInfo): Promise<void> {
    const confirmed = await this.dialogService.confirm({
      title: 'Är du säker',
      body: 'Uppdatera fakturan?',
      confirmText: 'OK',
      cancelText: 'Avbryt',
    });

    if (!confirmed) {
      return;
    }

    this.erpService
      .update(
        {
          id: inv.ext_statement_id!,
          redirect_uri: this.oauthUtilService.fullUrl(),
        },
        erpInfo
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          console.log('Success update ERP');
        },
        error: async (error: { status: number }) => {
          if (error.status === 433) {
            this.oauthUtilService.oauthLoginHandler(error);
          } else {
            await this.dialogService.alert({
              title: 'Ajdå, något gick fel ...',
              body: JSON.stringify(error),
              buttonText: 'OK',
              variant: 'danger',
            });
          }
        },
      });
  }

  arAdmin(): boolean {
    return true;
  } // Replace with Login service
  arSalj(): boolean {
    return true;
  } // Replace with Login service
}
